import styled from 'styled-components';
import { BtnBlue, BtnGhost } from '../Button';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import useBackHistory from '../../hooks/useBackHistory';

const Root = styled.div<{ $buttonPosition?: { top: number; left: number; width: number; height: number } }>`
  position: fixed;
  display: flex;
  align-items: ${(props) => (props.$buttonPosition ? 'flex-start' : 'center')};
  justify-content: ${(props) => (props.$buttonPosition ? 'flex-start' : 'center')};
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 5010;
  opacity: 0;
  transition: all 0.3s ease;
  visibility: hidden;
  pointer-events: none;

  &.active {
    background-color: rgba(0, 0, 0, 0.5);
    visibility: visible;
    pointer-events: auto;
    opacity: 1;
  }

  > div {
    height: 100%;
  }

  .modal {
    width: 24rem !important;
    max-height: 50vh;
    height: calc(100vh - 150px) !important;
    border-radius: 1rem;
    background: var(--board);
    transform: translateY(40px);
    transition: all 0.3s ease;
    ${(props) =>
      props.$buttonPosition
        ? `
      position: absolute;
      top: ${props.$buttonPosition.top + props.$buttonPosition.height + 8}px;
      left: ${props.$buttonPosition.left}px;
      margin: 0;
      max-width: calc(100vw - 32px);
    `
        : ''}

    &.active {
      transform: translateY(0);
    }

    @media screen and (max-width: 768px) {
      & {
        position: absolute;
        bottom: 0;
        width: 100% !important;
        height: 70vh !important;
        max-height: initial;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        transform: translateY(100%);
      }

      &.active {
        transform: translateY(0);
      }
    }

    .inputForm-head {
      display: flex;
      width: 100%;
      justify-content: space-between;
      padding: 0.5rem 1rem;
      border-bottom: 1px solid var(--outline);

      .medium {
        display: flex;
        align-items: center;
        font-size: 1rem;

        span {
          font-size: 22px;
        }
      }

      .close-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: auto;
        height: auto;
        padding: 0;
      }
    }

    .inputForm-body {
      display: flex;
      flex-direction: column;
      height: calc(100% - 52px);

      .calendarLabel,
      .inputDash {
        color: var(--text-secondary);
      }

      .filter-option-wrapper {
        height: -webkit-fill-available;
        padding: 0 1rem;
        overflow-y: auto;

        .filter-option-date {
          padding: 24px 0;

          .flex-basic {
            background: var(--tonal);

            input {
              text-align: center;
            }
          }
        }

        .filter-option-col {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .filter-option-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;

          > label {
            min-width: 7rem;
          }

          > .inputForm-col {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            gap: 1rem !important;

            > div > ul {
              width: inherit;
              > li {
                width: inherit;
                max-width: inherit;
              }
            }
          }

          > div {
            flex: 1;
            > ul {
              width: inherit;

              > li {
                width: inherit;
                max-width: inherit;
              }
            }
          }

          &.point {
            .point-group {
              display: flex;
              align-items: center;
              justify-content: flex-end;
              max-width: 12rem;
              gap: 0.5rem;

              input {
                text-align: center;
              }
            }
          }
        }

        .filter-underline {
          border-bottom: 1px solid var(--outline);
        }

        .date-one {
          .inputForm-col {
            position: relative;
            width: 160px;

            .calendarFooter {
              position: absolute;
              right: 10px;
              top: 8px;
              color: var(--text-tertiary);
            }
          }
        }

        .filter-option-cont {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          padding: 1.5rem 0;
        }
      }

      .modal-footer {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1rem;
        gap: 0.5rem;
        border-top: 1px solid var(--outline);

        .filter-search-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 1;
          height: 3rem;
          gap: 0.25rem;
        }

        .filter-reset-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 3rem;
          height: 3rem;
          padding: 0;
          border: 1px solid var(--outline);
          border-radius: 0.5rem;
        }
      }
    }
  }
`;

interface Props {
  children: React.ReactNode;
  openModal?: any;
  setOpenModal: any;
  onClickSearch: (ref: any) => void;
  onClickReset: () => void;
  isSearching?: boolean;
}

const SearchFilterModal = ({ children, openModal, setOpenModal, onClickSearch, onClickReset, isSearching = false }: Props) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const [buttonPosition, setButtonPosition] = useState<{ top: number; left: number; width: number; height: number } | undefined>();

  const onClickClose = () => {
    modalRef.current?.classList.remove('active');
    setOpenModal({ status: false, type: 'filter' });
  };

  const { onBackPrev } = useBackHistory({ isOpen: openModal.status, onPrev: onClickClose, pageName: 'search_filter_modal' });

  useEffect(() => {
    if (openModal?.status && openModal?.type === 'filter') {
      // 데스크톱에서 버튼 위치 계산
      if (window.innerWidth > 1024 && openModal?.buttonRef?.current) {
        const { top, left: buttonLeft, width, height } = openModal.buttonRef.current.getBoundingClientRect();
        const modalWidth = 384; // 24rem = 384px

        // 버튼 아래에 모달 배치, 화면 오른쪽 끝을 넘지 않도록 조정
        let left = buttonLeft;
        if (left + modalWidth > window.innerWidth - 16) {
          left = window.innerWidth - modalWidth - 16;
        }

        const position = { top, left: Math.max(16, left), width, height };
        setButtonPosition(position);
      } else {
        setButtonPosition(undefined);
      }

      requestAnimationFrame(() => {
        modalRef.current?.classList.add('active');
      });
    } else {
      modalRef.current?.classList.remove('active');
    }
  }, [openModal?.status, openModal?.type, openModal?.buttonRef]);

  // 모달 애니메이션이 완료된 후 buttonPosition 제거
  const handleTransitionEnd = (e: React.TransitionEvent) => {
    if (e.target === modalRef.current && !openModal?.status) {
      setButtonPosition(undefined);
    }
  };

  return (
    <Root role='presentation' className={`${openModal?.status && openModal?.type === 'filter' ? 'active' : ''}`} $buttonPosition={buttonPosition} onClick={onBackPrev}>
      <div
        role='presentation'
        className='modal'
        ref={modalRef}
        onClick={(event) => {
          event.stopPropagation();
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        <div className='inputForm-head'>
          <h5 className='medium'>
            <span className='material-symbols-rounded'>filter_alt</span>
            {t('검색 필터')}
          </h5>
          <BtnGhost onClick={onBackPrev} className='close-btn'>
            <span className='material-symbols-rounded'>close</span>
          </BtnGhost>
        </div>
        <div className='inputForm-body'>
          {children}
          <div className='modal-footer flex-end'>
            <BtnBlue className='filter-search-btn' onClick={() => onClickSearch(modalRef)} disabled={isSearching} aria-busy={isSearching}>
              <span className='material-symbols-rounded'>search</span>
              검색
            </BtnBlue>
            <BtnGhost className='filter-reset-btn' onClick={onClickReset}>
              <span className='material-symbols-rounded'>cached</span>
            </BtnGhost>
          </div>
        </div>
      </div>
    </Root>
  );
};

export default SearchFilterModal;
